import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Building2, Home as HomeIcon, Briefcase, Calendar, MapPin,
  Phone, Mail, UserCheck, FileText, Save, MessageSquare, CheckCircle2, CreditCard, type LucideIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Lead, EstadoLead, TipoImovel,
  getLeads, getInstaladores, updateLead, Instalador,
  ESTADO_LABELS, ESTADO_COLORS, TIPO_IMOVEL_LABELS,
  formatDate, formatDateShort, generateLeadCode
} from '@/lib/index';

const TIPO_ICON: Record<TipoImovel, LucideIcon> = {
  predio: Building2,
  moradia: HomeIcon,
  escritorio: Briefcase,
};

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [lead, setLead] = useState<Lead | null>(null);
  const [instaladores, setInstaladores] = useState<Instalador[]>([]);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailsSent, setEmailsSent] = useState(0);

  // editable fields
  const [estado, setEstado] = useState<EstadoLead>('novo');
  const [instaladorIds, setInstaladorIds] = useState<string[]>([]);
  const [numeroFatura, setNumeroFatura] = useState('');
  const [notasInternas, setNotasInternas] = useState('');
  // pagamentos
  const [sinalPago, setSinalPago] = useState(false);
  const [sinalPagoData, setSinalPagoData] = useState('');
  const [pagamentoFinalPago, setPagamentoFinalPago] = useState(false);
  const [pagamentoFinalData, setPagamentoFinalData] = useState('');
  const [instaladorPago, setInstaladorPago] = useState(false);
  const [instaladorPagoData, setInstaladorPagoData] = useState('');

  // Guardar quais instaladores já tinham sido notificados (os que já estavam antes de guardar)
  const [instaladoresNotificados, setInstaladoresNotificados] = useState<string[]>([]);

  useEffect(() => {
    async function load() {
      const [leads, insList] = await Promise.all([getLeads(), getInstaladores()]);
      const found = leads.find((l) => l.id === id) ?? null;
      setLead(found);
      if (found) {
        setEstado(found.estado);
        const ids = found.instaladorIds ?? (found.instaladorId ? [found.instaladorId] : []);
        setInstaladorIds(ids);
        setInstaladoresNotificados(ids);
        setNumeroFatura(found.numeroFatura ?? '');
        setNotasInternas(found.notasInternas ?? '');
        setSinalPago(found.sinalPago ?? false);
        setSinalPagoData(found.sinalPagoData ?? '');
        setPagamentoFinalPago(found.pagamentoFinalPago ?? false);
        setPagamentoFinalData(found.pagamentoFinalData ?? '');
        setInstaladorPago(found.instaladorPago ?? false);
        setInstaladorPagoData(found.instaladorPagoData ?? '');
      }
      setInstaladores(insList);
    }
    load();
  }, [id]);

  const handleSave = async () => {
    if (!id || !lead) return;
    setSaving(true);
    setEmailsSent(0);

    const updated = await updateLead(id, {
      estado,
      instaladorIds: instaladorIds.length > 0 ? instaladorIds : undefined,
      instaladorId: instaladorIds[0] ?? undefined,
      numeroFatura: numeroFatura || undefined,
      notasInternas: notasInternas || undefined,
      sinalPago, sinalPagoData: sinalPagoData || undefined,
      pagamentoFinalPago, pagamentoFinalData: pagamentoFinalData || undefined,
      instaladorPago, instaladorPagoData: instaladorPagoData || undefined,
    });

    if (updated) {
      // Enviar email apenas aos instaladores NOVOS (não notificados anteriormente)
      const novosInstaladores = instaladorIds.filter((iid) => !instaladoresNotificados.includes(iid));
      for (const iid of novosInstaladores) {
        const ins = instaladores.find((i) => i.id === iid);
        if (!ins?.email) continue;
        const code = generateLeadCode(lead.id);
        await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            access_key: import.meta.env.VITE_WEB3FORMS_KEY,
            to: ins.email,
            subject: `[AT CONTERA] Nova obra atribuída — ${code} — ${lead.localidade}`,
            from_name: 'AT CONTERA',
            replyto: 'comercial@contera.pt',
            message: [
              `Olá ${ins.nome},`,
              ``,
              `A AT CONTERA atribuiu-lhe uma nova obra. Por favor entre em contacto com o cliente.`,
              ``,
              `══════════════════════════════════`,
              `📋 REFERÊNCIA: ${code}`,
              `══════════════════════════════════`,
              ``,
              `👤 CLIENTE`,
              `   Nome: ${lead.nome}`,
              `   Telefone: ${lead.telefone}`,
              `   Email: ${lead.email}`,
              ``,
              `📍 LOCALIZAÇÃO`,
              `   Morada: ${lead.morada}`,
              `   Localidade: ${lead.localidade} · ${lead.codigoPostal}`,
              `   Tipo de imóvel: ${{ predio: 'Prédio', moradia: 'Moradia', escritorio: 'Escritório' }[lead.tipoImovel]}`,
              ``,
              `📅 DATA PREFERIDA PARA VISITA: ${lead.dataVisita ? new Date(lead.dataVisita).toLocaleDateString('pt-PT') : '—'}`,
              lead.notas ? `\n📝 NOTAS DO CLIENTE:\n   ${lead.notas}` : '',
              ``,
              `──────────────────────────────────`,
              `⚠️  IMPORTANTE — PAGAMENTOS`,
              ``,
              `💳 TODOS OS PAGAMENTOS SÃO FEITOS À AT CONTERA E PELA AT CONTERA.`,
              `   Não existe qualquer dinheiro a circular fora da AT CONTERA.`,
              ``,
              `   → O cliente paga 30% de sinal à AT CONTERA na adjudicação`,
              `   → A AT CONTERA fornece o material ao instalador`,
              `   → O cliente paga os 70% restantes à AT CONTERA na conclusão`,
              `   → A AT CONTERA paga-lhe no prazo de 48 horas após conclusão`,
              ``,
              `   Não aceite nem solicite qualquer pagamento direto ao cliente.`,
              `   Este serviço é 100% gratuito e transparente para ambas as partes.`,
              ``,
              `──────────────────────────────────`,
              `• Contacte o cliente para agendar visita`,
              `• O orçamento deve ser enviado à AT CONTERA para aprovação`,
              `• Todos os trabalhos têm Garantia de 3 Anos AT CONTERA`,
              ``,
              `📞 Dúvidas: comercial@contera.pt`,
              `──────────────────────────────────`,
              `AT CONTERA · 66 anos de experiência · Instaladores Certificados Comelit`,
            ].filter(Boolean).join('\n'),
          }),
        });
      }

      // Atualizar lista de notificados
      setInstaladoresNotificados(instaladorIds);
      setLead(updated);
      setEmailsSent(novosInstaladores.length);
      setSaved(true);
      setSaving(false);
      setTimeout(() => { setSaved(false); setEmailsSent(0); }, 3500);
    } else {
      setSaving(false);
    }
  };

  const toggleInstalador = (id: string) => {
    setInstaladorIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  if (!lead) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Pedido não encontrado.</p>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={15} className="mr-2" />Voltar ao Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const TipoIcon = TIPO_ICON[lead.tipoImovel];
  const instaladoresSelecionados = instaladores.filter((i) =>
    instaladorIds.includes(i.id) || lead.instaladorIds?.includes(i.id) || lead.instaladorId === i.id
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border bg-card/90 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={15} className="mr-2" />Voltar
          </Button>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-muted-foreground">{generateLeadCode(lead.id)}</span>
            <Badge className={`text-xs border ${ESTADO_COLORS[lead.estado]}`} variant="outline">
              {ESTADO_LABELS[lead.estado]}
            </Badge>
          </div>
          <Button size="sm" onClick={handleSave} disabled={saved || saving}>
            {saving ? (
              <><span className="animate-spin mr-1.5">⏳</span>A guardar...</>
            ) : saved ? (
              <><CheckCircle2 size={14} className="mr-1.5 text-green-300" />{emailsSent > 0 ? `Guardado · ${emailsSent} email${emailsSent > 1 ? 's' : ''} enviado${emailsSent > 1 ? 's' : ''}` : 'Guardado'}</>
            ) : (
              <><Save size={14} className="mr-1.5" />Guardar</>
            )}
          </Button>
        </div>
      </header>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 35 }}
        className="max-w-4xl mx-auto px-4 py-8"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: dados do cliente */}
          <div className="lg:col-span-2 space-y-5">
            {/* Cliente */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Phone size={16} className="text-primary" />
                Dados do Cliente
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Nome</p>
                  <p className="font-medium text-foreground mt-0.5">{lead.nome}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Telefone</p>
                  <p className="font-medium text-foreground mt-0.5 flex items-center gap-1">
                    <Phone size={12} className="text-muted-foreground" />{lead.telefone}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="font-medium text-foreground mt-0.5 flex items-center gap-1">
                    <Mail size={12} className="text-muted-foreground" />{lead.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Localização */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <MapPin size={16} className="text-primary" />
                Localização e Imóvel
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">Morada</p>
                  <p className="font-medium text-foreground mt-0.5">{lead.morada}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Localidade</p>
                  <p className="font-medium text-foreground mt-0.5">{lead.localidade}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Código Postal</p>
                  <p className="font-medium text-foreground mt-0.5 font-mono">{lead.codigoPostal}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tipo de Imóvel</p>
                  <p className="font-medium text-foreground mt-0.5 flex items-center gap-1.5">
                    <TipoIcon size={14} className="text-muted-foreground" />
                    {TIPO_IMOVEL_LABELS[lead.tipoImovel]}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Data de Visita Preferida</p>
                  <p className="font-medium text-foreground mt-0.5 flex items-center gap-1.5">
                    <Calendar size={14} className="text-muted-foreground" />
                    {formatDateShort(lead.dataVisita)}
                  </p>
                </div>
              </div>
              {lead.notas && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-1">Notas do cliente</p>
                  <p className="text-sm text-foreground bg-muted/50 rounded-lg p-3 leading-relaxed">{lead.notas}</p>
                </div>
              )}
            </div>

            {/* Notas internas */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <MessageSquare size={16} className="text-primary" />
                Notas Internas
              </h2>
              <Textarea
                placeholder="Anotações internas da equipa AT CONTERA..."
                className="resize-none h-28"
                value={notasInternas}
                onChange={(e) => setNotasInternas(e.target.value)}
              />
            </div>
          </div>

          {/* Right: gestão */}
          <div className="space-y-5">
            {/* Estado */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <FileText size={16} className="text-primary" />
                Gestão do Pedido
              </h2>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Estado</Label>
                  <Select value={estado} onValueChange={(v) => setEstado(v as EstadoLead)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.entries(ESTADO_LABELS) as [EstadoLead, string][]).map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Instaladores atribuídos</Label>
                  {instaladores.length === 0 ? (
                    <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-3">
                      Sem instaladores disponíveis. Aprove instaladores na aba "Rede Comelit" do dashboard.
                    </p>
                  ) : (
                    <div className="border border-border rounded-xl overflow-hidden">
                      {instaladores.map((ins, idx) => {
                        const selected = instaladorIds.includes(ins.id);
                        const jaNotificado = instaladoresNotificados.includes(ins.id);
                        return (
                          <button
                            key={ins.id}
                            type="button"
                            onClick={() => toggleInstalador(ins.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${idx > 0 ? 'border-t border-border' : ''} ${selected ? 'bg-primary/8' : 'hover:bg-muted/50'}`}
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${selected ? 'bg-primary border-primary' : 'border-border'}`}>
                              {selected && <CheckCircle2 size={10} className="text-primary-foreground" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${selected ? 'text-foreground' : 'text-muted-foreground'}`}>{ins.nome}</p>
                              <p className="text-xs text-muted-foreground">{ins.zona}</p>
                            </div>
                            {jaNotificado && (
                              <Badge className="text-xs bg-blue-50 text-blue-600 border border-blue-200 shrink-0">📧 Notificado</Badge>
                            )}
                            {selected && !jaNotificado && (
                              <Badge className="text-xs bg-orange-50 text-orange-600 border border-orange-200 shrink-0">Novo ✓</Badge>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {instaladorIds.length > 0 && (
                    <p className="text-xs text-muted-foreground">{instaladorIds.length} instalador{instaladorIds.length > 1 ? 'es' : ''} selecionado{instaladorIds.length > 1 ? 's' : ''}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Garantia */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h2 className="font-semibold text-foreground mb-1 flex items-center gap-2">
                <UserCheck size={16} className="text-primary" />
                Garantia AT CONTERA
              </h2>
              <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                Após conclusão da obra, registe aqui o número de fatura para arquivo da garantia.
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs">Número de Fatura</Label>
                <Input
                  placeholder="Ex: FT 2026/00123"
                  className="font-mono"
                  value={numeroFatura}
                  onChange={(e) => setNumeroFatura(e.target.value)}
                />
              </div>
              {lead.numeroFatura && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs text-green-700 font-medium flex items-center gap-1.5">
                    <CheckCircle2 size={13} />Fatura registada
                  </p>
                  <p className="font-mono text-sm text-green-800 font-semibold mt-1">{lead.numeroFatura}</p>
                </div>
              )}
            </div>

            {/* ── GESTÃO FINANCEIRA ── */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h2 className="font-semibold text-foreground mb-1 flex items-center gap-2">
                <CreditCard size={16} className="text-primary" />
                Gestão Financeira
              </h2>
              <p className="text-xs text-muted-foreground mb-5 leading-relaxed">
                Modelo de pagamento AT CONTERA: <strong>30% adjudicação + 70% conclusão</strong>. Instalador pago em ≤48h após obra concluída.
              </p>

              <div className="space-y-4">
                {/* Passo 1 — Sinal 30% */}
                <div className={`rounded-xl border p-4 transition-colors ${sinalPago ? 'bg-green-50 border-green-200' : 'bg-muted/30 border-border'}`}>
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => { setSinalPago(!sinalPago); if (!sinalPago && !sinalPagoData) setSinalPagoData(new Date().toISOString().split('T')[0]); }}
                      className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${sinalPago ? 'bg-green-500 border-green-500' : 'border-muted-foreground'}`}
                    >
                      {sinalPago && <CheckCircle2 size={12} className="text-white" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-muted-foreground">1</span>
                        <p className={`font-semibold text-sm ${sinalPago ? 'text-green-800' : 'text-foreground'}`}>
                          Sinal recebido (30%)
                        </p>
                        {sinalPago && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Pago</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">Cliente pagou 30% de adjudicação à AT CONTERA</p>
                      {sinalPago && (
                        <div className="mt-2">
                          <Label className="text-xs text-green-700">Data do pagamento</Label>
                          <Input
                            type="date"
                            className="mt-1 h-8 text-xs"
                            value={sinalPagoData}
                            onChange={(e) => setSinalPagoData(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Passo 2 — Pagamento Final 70% */}
                <div className={`rounded-xl border p-4 transition-colors ${pagamentoFinalPago ? 'bg-green-50 border-green-200' : 'bg-muted/30 border-border'}`}>
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => { setPagamentoFinalPago(!pagamentoFinalPago); if (!pagamentoFinalPago && !pagamentoFinalData) setPagamentoFinalData(new Date().toISOString().split('T')[0]); }}
                      className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${pagamentoFinalPago ? 'bg-green-500 border-green-500' : 'border-muted-foreground'}`}
                    >
                      {pagamentoFinalPago && <CheckCircle2 size={12} className="text-white" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-muted-foreground">2</span>
                        <p className={`font-semibold text-sm ${pagamentoFinalPago ? 'text-green-800' : 'text-foreground'}`}>
                          Pagamento final recebido (70%)
                        </p>
                        {pagamentoFinalPago && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Recebido</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">Obra concluída — cliente pagou os restantes 70% à AT CONTERA</p>
                      {pagamentoFinalPago && (
                        <div className="mt-2">
                          <Label className="text-xs text-green-700">Data do pagamento</Label>
                          <Input
                            type="date"
                            className="mt-1 h-8 text-xs"
                            value={pagamentoFinalData}
                            onChange={(e) => setPagamentoFinalData(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Passo 3 — Pago ao Instalador */}
                <div className={`rounded-xl border p-4 transition-colors ${instaladorPago ? 'bg-blue-50 border-blue-200' : pagamentoFinalPago ? 'bg-orange-50 border-orange-200' : 'bg-muted/30 border-border'}`}>
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => { setInstaladorPago(!instaladorPago); if (!instaladorPago && !instaladorPagoData) setInstaladorPagoData(new Date().toISOString().split('T')[0]); }}
                      className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${instaladorPago ? 'bg-blue-500 border-blue-500' : 'border-muted-foreground'}`}
                    >
                      {instaladorPago && <CheckCircle2 size={12} className="text-white" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-muted-foreground">3</span>
                        <p className={`font-semibold text-sm ${instaladorPago ? 'text-blue-800' : 'text-foreground'}`}>
                          Instalador pago pela AT CONTERA
                        </p>
                        {instaladorPago && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">✓ Pago</span>}
                        {!instaladorPago && pagamentoFinalPago && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium animate-pulse">⚠ Pendente — prazo 48h</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">AT CONTERA transferiu o pagamento ao instalador (≤48h após conclusão)</p>
                      {instaladorPago && (
                        <div className="mt-2">
                          <Label className="text-xs text-blue-700">Data do pagamento</Label>
                          <Input
                            type="date"
                            className="mt-1 h-8 text-xs"
                            value={instaladorPagoData}
                            onChange={(e) => setInstaladorPagoData(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Resumo */}
                {(sinalPago || pagamentoFinalPago || instaladorPago) && (
                  <div className="bg-muted/30 rounded-lg px-4 py-3 text-xs space-y-1 border border-border">
                    <p className="font-semibold text-foreground mb-2">Resumo financeiro</p>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sinal recebido (30%)</span>
                      <span className={sinalPago ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
                        {sinalPago ? `✓ ${sinalPagoData ? formatDate(sinalPagoData) : '—'}` : 'Pendente'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Pagamento final (70%)</span>
                      <span className={pagamentoFinalPago ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
                        {pagamentoFinalPago ? `✓ ${pagamentoFinalData ? formatDate(pagamentoFinalData) : '—'}` : 'Pendente'}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-border pt-1 mt-1">
                      <span className="text-muted-foreground">Pago ao instalador</span>
                      <span className={instaladorPago ? 'text-blue-600 font-medium' : pagamentoFinalPago ? 'text-orange-600 font-medium' : 'text-muted-foreground'}>
                        {instaladorPago ? `✓ ${instaladorPagoData ? formatDate(instaladorPagoData) : '—'}` : pagamentoFinalPago ? '⚠ Pagar ≤48h' : 'Aguarda conclusão'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-muted/50 border border-border rounded-2xl p-4 text-xs text-muted-foreground space-y-1.5">
              <p className="flex justify-between">
                <span>Referência</span>
                <span className="font-mono font-medium text-foreground">{generateLeadCode(lead.id)}</span>
              </p>
              <p className="flex justify-between">
                <span>Criado em</span>
                <span>{formatDate(lead.criadoEm)}</span>
              </p>
            </div>

            <Button className="w-full" onClick={handleSave} disabled={saved || saving}>
              {saving ? (
                <><span className="animate-spin mr-2">⏳</span>A guardar e enviar emails...</>
              ) : saved ? (
                <><CheckCircle2 size={15} className="mr-2" />{emailsSent > 0 ? `Guardado · ${emailsSent} email${emailsSent > 1 ? 's' : ''} enviado${emailsSent > 1 ? 's' : ''}` : 'Guardado com sucesso!'}</>
              ) : (
                <><Save size={15} className="mr-2" />Guardar alterações</>
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
